/**
 * Composant - Dashboard
 *
 * Accueil du caissier (BIOTRANSAC V2 — UNACOOPEC) :
 * - bandeau d'accueil : salutation, date, état de connexion, actualisation, actions rapides ;
 * - indicateurs du jour comparés à la veille ;
 * - activité des 7 derniers jours, répartition du jour, points d'attention ;
 * - dernières opérations.
 *
 * Source unique : historique CBS du caissier (useOperationHistory). Le CBS ne fournit que la date
 * des opérations (sans l'heure) : pas de vue horaire.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  History,
  Minus,
  RefreshCw,
  Scale,
  TrendingDown,
  TrendingUp,
  Activity,
  WifiOff,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useOperationHistory } from '../../hooks/useOperationHistory';
import type { HistoryTransaction } from '../../hooks/useOperationHistory';
import { useAuth } from '../../contexts/AuthContext';
import { DEFAULT_THRESHOLDS } from './types';
import { dateCourte, fcfa, filtrer, iso, nombre, periodeRapide, totaux } from '../history/analyseOperations';
import '../history/pages.css';
import './dashboard.css';

const VERT = '#016951';
const ROSE = '#e11d48';
const COMPACT = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 });
const FORMAT_DATE_LONGUE = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const FORMAT_JOUR_COURT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit' });
const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

const majuscule = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Libellé relatif d'une date d'opération (le CBS ne donne pas l'heure) */
function jourRelatif(d: Date): string {
  const j = iso(d);
  if (j === periodeRapide('jour').du) return "Aujourd'hui";
  if (j === periodeRapide('hier').du) return 'Hier';
  return dateCourte(d);
}

/** Connexion réseau, mise à jour en direct */
function useEnLigne(): boolean {
  const [enLigne, setEnLigne] = useState(() => navigator.onLine);
  useEffect(() => {
    const maj = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', maj);
    window.addEventListener('offline', maj);
    return () => {
      window.removeEventListener('online', maj);
      window.removeEventListener('offline', maj);
    };
  }, []);
  return enLigne;
}

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { transactions, loading, error, refresh } = useOperationHistory();
  const enLigne = useEnLigne();

  // Heure de la dernière synchronisation réussie
  const [majA, setMajA] = useState<Date | null>(null);
  useEffect(() => {
    if (!loading && !error) setMajA(new Date());
  }, [loading, error, transactions]);

  const maintenant = new Date();
  const prenom = (user?.displayName || user?.username || '').trim();
  const salutation = maintenant.getHours() >= 18 ? 'Bonsoir' : 'Bonjour';

  const jour = useMemo(() => filtrer(transactions, { periode: periodeRapide('jour') }), [transactions]);
  const veille = useMemo(() => filtrer(transactions, { periode: periodeRapide('hier') }), [transactions]);
  const k = useMemo(() => totaux(jour), [jour]);
  const kHier = useMemo(() => totaux(veille), [veille]);

  // 7 derniers jours, y compris les jours sans opération
  const semaine = useMemo(() => {
    const jours = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { cle: iso(d), jour: majuscule(FORMAT_JOUR_COURT.format(d)).replace('.', ''), Versements: 0, Retraits: 0 };
    });
    const index = new Map(jours.map((j) => [j.cle, j]));
    for (const t of transactions) {
      const j = index.get(iso(t.date));
      if (!j) continue;
      if (t.type === 'versement') j.Versements += t.amount;
      else j.Retraits += t.amount;
    }
    return jours;
  }, [transactions]);
  const semaineVide = semaine.every((j) => j.Versements === 0 && j.Retraits === 0);

  // Points d'attention : opérations du jour au-delà du seuil de montant élevé
  const attention = useMemo(
    () => jour.filter((t) => t.amount >= DEFAULT_THRESHOLDS.highAmountThreshold).sort((a, b) => b.amount - a.amount),
    [jour],
  );

  const dernieres = transactions.slice(0, 8);
  const volumeJour = k.montantVersements + k.montantRetraits;
  const partVersements = volumeJour ? (k.montantVersements / volumeJour) * 100 : 0;

  return (
    <div className="pg">
      {/* ── Bandeau d'accueil ── */}
      <section className="tb-hero">
        <div className="tb-hero-haut">
          <div>
            <h1>
              {salutation}
              {prenom && `, ${prenom}`}
            </h1>
            <p className="date">{majuscule(FORMAT_DATE_LONGUE.format(maintenant))}</p>
          </div>
          <div className="tb-etat">
            <span className={`tb-puce ${enLigne ? '' : 'hs'}`}>
              {enLigne ? <span className="point" /> : <WifiOff />}
              {enLigne ? 'Connecté' : 'Hors ligne'}
            </span>
            {majA && <span className="tb-puce">Mis à jour à {FORMAT_HEURE.format(majA)}</span>}
            <button type="button" className="tb-btn-clair" onClick={() => refresh()} disabled={loading}>
              <RefreshCw className={loading ? 'pg-tourne' : undefined} /> Actualiser
            </button>
          </div>
        </div>

        <nav className="tb-actions" aria-label="Actions rapides">
          <button type="button" className="tb-action principale" onClick={() => navigate('/deposits')}>
            <span className="ico">
              <ArrowDownLeft />
            </span>
            <span>
              <strong>Nouveau versement</strong>
              <small>Créditer un compte client</small>
            </span>
          </button>
          <button type="button" className="tb-action principale retrait" onClick={() => navigate('/withdrawals')}>
            <span className="ico">
              <ArrowUpRight />
            </span>
            <span>
              <strong>Nouveau retrait</strong>
              <small>Avec authentification biométrique</small>
            </span>
          </button>
          <button type="button" className="tb-action" onClick={() => navigate('/history')}>
            <span className="ico">
              <History />
            </span>
            <span>
              <strong>Historique</strong>
              <small>Opérations et duplicatas</small>
            </span>
          </button>
          <button type="button" className="tb-action" onClick={() => navigate('/reports')}>
            <span className="ico">
              <BarChart3 />
            </span>
            <span>
              <strong>Rapports</strong>
              <small>Arrêté d&apos;activité de caisse</small>
            </span>
          </button>
        </nav>
      </section>

      {error && (
        <div className="pg-erreur" role="alert">
          <AlertCircle />
          <div>
            <strong>Les données de la caisse n&apos;ont pas pu être chargées.</strong> {error}{' '}
            <button type="button" className="tb-lien" onClick={() => refresh()}>
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* ── Indicateurs du jour ── */}
      <section className="pg-kpis" aria-label="Indicateurs du jour">
        <Indicateur
          ton="vert"
          icone={<ArrowDownLeft />}
          titre="Versé aujourd'hui"
          valeur={fcfa(k.montantVersements)}
          chargement={loading}
          sous={<><Tendance actuel={k.montantVersements} veille={kHier.montantVersements} />{nombre(k.nbVersements)} versement(s)</>}
        />
        <Indicateur
          ton="rose"
          icone={<ArrowUpRight />}
          titre="Retiré aujourd'hui"
          valeur={fcfa(k.montantRetraits)}
          chargement={loading}
          sous={<><Tendance actuel={k.montantRetraits} veille={kHier.montantRetraits} />{nombre(k.nbRetraits)} retrait(s)</>}
        />
        <Indicateur
          ton="bleu"
          icone={<Scale />}
          titre="Flux net du jour"
          valeur={`${k.net > 0 ? '+' : ''}${fcfa(k.net)}`}
          classeValeur={k.net > 0 ? 'pg-pos' : k.net < 0 ? 'pg-neg' : ''}
          chargement={loading}
          sous={<>Hier : {kHier.net > 0 ? '+' : ''}{fcfa(kHier.net)}</>}
        />
        <Indicateur
          ton="gris"
          icone={<Activity />}
          titre="Opérations du jour"
          valeur={nombre(k.nbOperations)}
          chargement={loading}
          sous={<><Tendance actuel={k.nbOperations} veille={kHier.nbOperations} />Hier : {nombre(kHier.nbOperations)}</>}
        />
      </section>

      <div className="tb-grille">
        {/* ── Colonne principale ── */}
        <div className="tb-colonne">
          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Activité des 7 derniers jours</h2>
              <button type="button" className="tb-lien" onClick={() => navigate('/reports')}>
                Rapport détaillé <ArrowRight />
              </button>
            </div>
            <div className="pg-carte-corps" style={{ height: 280 }}>
              {loading ? (
                <div className="pg-squelette" style={{ height: '100%' }} />
              ) : semaineVide ? (
                <div className="pg-vide" style={{ padding: '80px 16px' }}>
                  <BarChart3 />
                  <strong>Aucune opération sur les 7 derniers jours</strong>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={semaine} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="jour" tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => COMPACT.format(v)} width={52} />
                    <Tooltip
                      formatter={(v: number) => fcfa(v)}
                      cursor={{ fill: 'rgba(1,105,81,0.05)' }}
                      contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 13 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 13 }} />
                    <Bar dataKey="Versements" fill={VERT} radius={[4, 4, 0, 0]} maxBarSize={32} />
                    <Bar dataKey="Retraits" fill={ROSE} radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Dernières opérations</h2>
              <button type="button" className="tb-lien" onClick={() => navigate('/history')}>
                Tout l&apos;historique <ArrowRight />
              </button>
            </div>
            {loading ? (
              <div className="pg-carte-corps" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="pg-squelette" style={{ height: 18 }} />
                ))}
              </div>
            ) : dernieres.length === 0 ? (
              <div className="pg-vide">
                <Activity />
                <strong>Aucune opération pour le moment</strong>
                <p>Vos versements et retraits apparaîtront ici.</p>
              </div>
            ) : (
              <div className="pg-table-wrap">
                <table className="pg-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Opération</th>
                      <th>Client</th>
                      <th className="masquer-mobile">Référence</th>
                      <th className="droite">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dernieres.map((t) => (
                      <LigneOperation key={t.id} t={t} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* ── Colonne latérale ── */}
        <div className="tb-colonne">
          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Répartition du jour</h2>
              <span>En montant</span>
            </div>
            <div className="pg-carte-corps tb-repartition">
              {loading ? (
                <div className="pg-squelette" style={{ height: 120 }} />
              ) : (
                <>
                  <div className="proportion" role="img" aria-label={`Versements ${Math.round(partVersements)} %, retraits ${Math.round(100 - partVersements)} %`}>
                    {volumeJour > 0 && (
                      <>
                        <div className="v" style={{ width: `${partVersements}%` }} />
                        <div className="r" style={{ width: `${100 - partVersements}%` }} />
                      </>
                    )}
                  </div>
                  <div className="tb-legende">
                    <div>
                      <span className="lib">
                        <i style={{ background: VERT }} /> Versements {volumeJour > 0 && `· ${Math.round(partVersements)} %`}
                      </span>
                      <div className="mt">{fcfa(k.montantVersements)}</div>
                      <div className="nb">{nombre(k.nbVersements)} opération(s)</div>
                    </div>
                    <div>
                      <span className="lib">
                        <i style={{ background: ROSE }} /> Retraits {volumeJour > 0 && `· ${Math.round(100 - partVersements)} %`}
                      </span>
                      <div className="mt">{fcfa(k.montantRetraits)}</div>
                      <div className="nb">{nombre(k.nbRetraits)} opération(s)</div>
                    </div>
                  </div>
                  <dl className="tb-chiffres">
                    <div>
                      <dt>Versement moyen</dt>
                      <dd>{fcfa(k.moyenneVersement)}</dd>
                    </div>
                    <div>
                      <dt>Retrait moyen</dt>
                      <dd>{fcfa(k.moyenneRetrait)}</dd>
                    </div>
                    <div>
                      <dt>Plus grosse opération</dt>
                      <dd>{k.plusGrosse ? `${fcfa(k.plusGrosse.amount)} (${k.plusGrosse.type})` : '—'}</dd>
                    </div>
                  </dl>
                </>
              )}
            </div>
          </section>

          <section className="pg-carte">
            <div className="pg-carte-tete">
              <h2>Points d&apos;attention</h2>
              <span>Aujourd&apos;hui</span>
            </div>
            <div className="pg-carte-corps">
              {loading ? (
                <div className="pg-squelette" style={{ height: 48 }} />
              ) : attention.length === 0 && enLigne ? (
                <div className="tb-ok">
                  <CheckCircle2 />
                  Aucune opération au-delà de {fcfa(DEFAULT_THRESHOLDS.highAmountThreshold)} aujourd&apos;hui.
                </div>
              ) : (
                <ul className="tb-attention">
                  {!enLigne && (
                    <li>
                      <span className="ico">
                        <WifiOff />
                      </span>
                      <div>
                        <strong>Connexion interrompue</strong>
                        <span>Les opérations sont impossibles tant que le poste est hors ligne.</span>
                      </div>
                    </li>
                  )}
                  {attention.slice(0, 5).map((t) => (
                    <li key={t.id}>
                      <span className="ico">
                        <AlertTriangle />
                      </span>
                      <div>
                        <strong>
                          {t.type === 'versement' ? 'Versement' : 'Retrait'} de {fcfa(t.amount)}
                        </strong>
                        <span>
                          {t.clientName || 'Client'} · {t.accountNumber}
                        </span>
                      </div>
                    </li>
                  ))}
                  {attention.length > 5 && <li className="pg-note">+ {attention.length - 5} autre(s) opération(s) de montant élevé</li>}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>

      <p className="pg-note">
        Données issues du CBS pour votre code caissier. Le CBS fournit la date des opérations sans l&apos;heure.
      </p>
    </div>
  );
}

function LigneOperation({ t }: { t: HistoryTransaction }) {
  const versement = t.type === 'versement';
  return (
    <tr>
      <td className="num">{jourRelatif(t.date)}</td>
      <td>
        <span className={`pg-type ${t.type}`}>
          {versement ? <ArrowDownLeft /> : <ArrowUpRight />}
          {versement ? 'Versement' : 'Retrait'}
        </span>
      </td>
      <td>
        <div className="pg-client">
          <div className="nom">{t.clientName || 'Client'}</div>
          <div className="cpt">{t.accountNumber}</div>
        </div>
      </td>
      <td className="masquer-mobile">
        <span className="pg-ref">{t.reference}</span>
      </td>
      <td className={`droite pg-montant ${versement ? 'pg-pos' : 'pg-neg'}`}>
        {versement ? '+' : '−'}
        {fcfa(t.amount)}
      </td>
    </tr>
  );
}

function Tendance({ actuel, veille }: { actuel: number; veille: number }) {
  if (veille === 0) return null;
  const ecart = ((actuel - veille) / veille) * 100;
  const arrondi = Math.round(Math.abs(ecart));
  const sens = arrondi === 0 ? 'stable' : ecart > 0 ? 'hausse' : 'baisse';
  return (
    <span className={`tb-tendance ${sens}`} title="Par rapport à la veille">
      {sens === 'hausse' ? <TrendingUp /> : sens === 'baisse' ? <TrendingDown /> : <Minus />}
      {arrondi} %
    </span>
  );
}

function Indicateur({
  ton,
  icone,
  titre,
  valeur,
  sous,
  classeValeur,
  chargement,
}: {
  ton: 'vert' | 'rose' | 'bleu' | 'gris';
  icone: React.ReactNode;
  titre: string;
  valeur: string;
  sous: React.ReactNode;
  classeValeur?: string;
  chargement?: boolean;
}) {
  return (
    <div className={`pg-kpi ${ton}`}>
      <div className="haut">
        <span>{titre}</span>
        <span className="pastille">{icone}</span>
      </div>
      {chargement ? <div className="pg-squelette" style={{ height: 24, width: '70%' }} /> : <div className={`val ${classeValeur ?? ''}`}>{valeur}</div>}
      <div className="sous">{chargement ? ' ' : sous}</div>
    </div>
  );
}
